import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  ShippingOfferDuration,
  ShippingPolicy,
  ShippingPolicyProps,
  formatMinimumOrderStartDate,
  offerDurationToDays,
} from '@/lib/types/shipping-policy';
import { cn } from '@/lib/utils';
import {
  enforceFreePickupPointPolicy,
  withoutFreeShippingConditions,
} from '@/lib/utils/shipping-policy-state';
import { storeAtom } from '@/states/store.state';
import { Currency } from '@/types/currency';
import { Store } from '@/types/store';
import Button from '@appchoose/button';
import { Checkbox } from '@appchoose/checkbox';
import Input, { InputAdornment } from '@appchoose/input';
import { RadioGroup, RadioGroupItem } from '@appchoose/radio-group';
import { useAtomValue } from 'jotai';
import { X } from 'lucide-react';

interface FormErrors {
  minimumOrderAmount?: string;
  pickupPointMinimumOrderAmount?: string;
}

type MinimumOrderMode = 'home' | 'pickupPoint';

const getRemovedMinimumOrderModes = (
  policy: ShippingPolicy
): Set<MinimumOrderMode> => {
  if (!policy.minimumOrderSplitByDeliveryMode) return new Set();

  const removedModes = new Set<MinimumOrderMode>();
  if (policy.minimumOrderAmount === null) removedModes.add('home');
  if (policy.pickupPointMinimumOrderAmount === null) {
    removedModes.add('pickupPoint');
  }
  return removedModes;
};

interface ShippingPolicyDisplayProps extends ShippingPolicyProps {
  embedded?: boolean;
  onCancel?: () => void;
  cancelLabel?: string;
  confirmLabel?: string;
  isSubmitting?: boolean;
}

function ShippingPolicyDisplay({
  policy,
  onPolicyChange,
  isLocked = false,
  open,
  onOpenChange,
  hubspotStartDate,
  embedded = false,
  onCancel,
  cancelLabel,
  confirmLabel,
  isSubmitting = false,
}: ShippingPolicyDisplayProps) {
  const { t, i18n } = useTranslation();
  const store = useAtomValue(storeAtom);

  const [tempPolicy, setTempPolicy] = useState<ShippingPolicy>(() =>
    enforceFreePickupPointPolicy(policy)
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [removedMinimumOrderModes, setRemovedMinimumOrderModes] = useState(
    getRemovedMinimumOrderModes(enforceFreePickupPointPolicy(policy))
  );

  const resetDraftState = useCallback(() => {
    const normalizedPolicy = enforceFreePickupPointPolicy(policy);
    setTempPolicy(normalizedPolicy);
    setErrors({});
    setRemovedMinimumOrderModes(getRemovedMinimumOrderModes(normalizedPolicy));
  }, [policy]);

  const allShippingFeesZero = policy.allShippingFeesZero;
  const bothOptionsEnabled =
    tempPolicy.minimumOrderEnabled && tempPolicy.timeLimitedEnabled;
  const showPickupPointMinimumAmount =
    tempPolicy.pickupPointEnabled && tempPolicy.minimumOrderSplitByDeliveryMode;
  const isPickupPointDeliveryFree =
    tempPolicy.pickupPointEnabled && tempPolicy.pickupPointDeliveryPrice === 0;
  const isHomeMinimumRemoved =
    showPickupPointMinimumAmount && removedMinimumOrderModes.has('home');
  const isPickupPointMinimumRemoved =
    showPickupPointMinimumAmount && removedMinimumOrderModes.has('pickupPoint');

  useEffect(() => {
    if (!open) resetDraftState();
  }, [open, resetDraftState]);

  const handleCancel = () => {
    if (isSubmitting) return;

    resetDraftState();
    if (onCancel) {
      onCancel();
    } else {
      onOpenChange(false);
    }
  };

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (isSubmitting && !nextOpen) return;

    if (!nextOpen) resetDraftState();
    onOpenChange(nextOpen);
  };

  // Keep local form state aligned with "all shipping fees = 0".
  // Persistence is handled in useShippingPolicy to avoid modal-coupled API calls.
  useEffect(() => {
    if (isLocked || !open || !allShippingFeesZero) return;

    setTempPolicy((current) =>
      enforceFreePickupPointPolicy(withoutFreeShippingConditions(current))
    );
    setRemovedMinimumOrderModes(new Set());
  }, [allShippingFeesZero, isLocked, open]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (tempPolicy.minimumOrderEnabled) {
      const isSplitMode =
        tempPolicy.pickupPointEnabled && showPickupPointMinimumAmount;
      const requiresHomeMinimum = !isSplitMode || !isHomeMinimumRemoved;
      const requiresPickupPointMinimum =
        isSplitMode && !isPickupPointMinimumRemoved;

      if (requiresHomeMinimum && tempPolicy.minimumOrderAmount === null) {
        newErrors.minimumOrderAmount = t(
          'components.shipping_policy_configurator.error_required'
        );
      }

      if (
        tempPolicy.minimumOrderAmount !== null &&
        tempPolicy.minimumOrderAmount < 1
      ) {
        newErrors.minimumOrderAmount = t(
          'components.shipping_policy_configurator.error_minimum_amount',
          {
            currency: store === Store.FR ? Currency.EUR : Currency.USD,
          }
        );
      }

      if (requiresPickupPointMinimum) {
        if (tempPolicy.pickupPointMinimumOrderAmount === null) {
          newErrors.pickupPointMinimumOrderAmount = t(
            'components.shipping_policy_configurator.error_required'
          );
        }

        if (
          tempPolicy.pickupPointMinimumOrderAmount !== null &&
          tempPolicy.pickupPointMinimumOrderAmount < 1
        ) {
          newErrors.pickupPointMinimumOrderAmount = t(
            'components.shipping_policy_configurator.error_minimum_amount',
            {
              currency: store === Store.FR ? Currency.EUR : Currency.USD,
            }
          );
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateMinimumOrderAmounts = (
    minimumOrderAmount: number | null,
    pickupPointMinimumOrderAmount: number | null
  ) => {
    setTempPolicy((current) => ({
      ...current,
      minimumOrderAmount,
      pickupPointMinimumOrderAmount,
    }));
  };

  const handleSave = () => {
    if (validateForm()) {
      onPolicyChange(tempPolicy);
    }
  };

  const handleMinimumOrderChange = (value: number | null) => {
    const newValue = isNaN(value as number) ? null : value;

    updateMinimumOrderAmounts(
      newValue,
      tempPolicy.pickupPointMinimumOrderAmount
    );

    if (newValue === null) {
      setErrors({
        ...errors,
        minimumOrderAmount: undefined,
      });
    } else if (newValue < 1) {
      setErrors({
        ...errors,
        minimumOrderAmount: t(
          'components.shipping_policy_configurator.error_minimum_amount',
          {
            currency: store === Store.FR ? Currency.EUR : Currency.USD,
          }
        ),
      });
    } else {
      setErrors({
        ...errors,
        minimumOrderAmount: undefined,
      });
    }
  };

  const handlePickupPointMinimumOrderChange = (value: number | null) => {
    const newValue = isNaN(value as number) ? null : value;

    updateMinimumOrderAmounts(tempPolicy.minimumOrderAmount, newValue);

    if (newValue === null) {
      setErrors({
        ...errors,
        pickupPointMinimumOrderAmount: undefined,
      });
    } else if (newValue < 1) {
      setErrors({
        ...errors,
        pickupPointMinimumOrderAmount: t(
          'components.shipping_policy_configurator.error_minimum_amount',
          {
            currency: store === Store.FR ? Currency.EUR : Currency.USD,
          }
        ),
      });
    } else {
      setErrors({
        ...errors,
        pickupPointMinimumOrderAmount: undefined,
      });
    }
  };

  const removeMinimumOrderAmount = (mode: MinimumOrderMode) => {
    const nextRemovedModes = new Set(removedMinimumOrderModes).add(mode);
    const shouldExitSplitMode = nextRemovedModes.size === 2;

    setRemovedMinimumOrderModes(
      shouldExitSplitMode ? new Set() : nextRemovedModes
    );
    setTempPolicy((current) => {
      if (shouldExitSplitMode) {
        return {
          ...current,
          minimumOrderEnabled: false,
          minimumOrderAmount: null,
          pickupPointMinimumOrderAmount: null,
          minimumOrderSplitByDeliveryMode: false,
        };
      }

      return {
        ...current,
        minimumOrderAmount: mode === 'home' ? null : current.minimumOrderAmount,
        pickupPointMinimumOrderAmount:
          mode === 'pickupPoint' ? null : current.pickupPointMinimumOrderAmount,
      };
    });
    setErrors({});
  };

  const restoreMinimumOrderAmount = (mode: MinimumOrderMode) => {
    const nextRemovedModes = new Set(removedMinimumOrderModes);
    nextRemovedModes.delete(mode);
    setRemovedMinimumOrderModes(nextRemovedModes);

    if (mode === 'home') {
      updateMinimumOrderAmounts(
        tempPolicy.pickupPointMinimumOrderAmount ?? 1,
        tempPolicy.pickupPointMinimumOrderAmount
      );
    } else {
      updateMinimumOrderAmounts(
        tempPolicy.minimumOrderAmount,
        tempPolicy.minimumOrderAmount ?? 1
      );
    }
    setErrors({});
  };

  const handleDurationChange = (duration: ShippingOfferDuration) => {
    setTempPolicy({
      ...tempPolicy,
      offerDuration: duration,
    });
  };

  // Ensure offerDuration is always a valid value when timeLimitedEnabled is true
  const currentDurationValue = tempPolicy.timeLimitedEnabled
    ? (tempPolicy.offerDuration ?? '24h')
    : '24h';

  const toggleTimeLimited = () => {
    setTempPolicy((current) => {
      const newEnabled = !current.timeLimitedEnabled;
      return {
        ...current,
        timeLimitedEnabled: newEnabled,
        offerDuration: newEnabled
          ? (current.offerDuration ?? '24h')
          : current.offerDuration,
      };
    });
  };

  const toggleMinimumOrder = () => {
    const isEnabling = !tempPolicy.minimumOrderEnabled;
    const isEnablingWithFreePickupPoint =
      isEnabling &&
      tempPolicy.pickupPointEnabled &&
      tempPolicy.pickupPointDeliveryPrice === 0;

    setTempPolicy((current) => {
      const newEnabled = !current.minimumOrderEnabled;
      const requiresSplitMode =
        newEnabled &&
        current.pickupPointEnabled &&
        current.pickupPointDeliveryPrice === 0;

      return {
        ...current,
        minimumOrderEnabled: newEnabled,
        minimumOrderSplitByDeliveryMode: newEnabled
          ? requiresSplitMode || current.minimumOrderSplitByDeliveryMode
          : false,
        minimumOrderAmount: newEnabled
          ? (current.minimumOrderAmount ?? 1)
          : current.minimumOrderAmount,
        pickupPointMinimumOrderAmount: requiresSplitMode
          ? null
          : current.pickupPointMinimumOrderAmount,
      };
    });
    if (isEnablingWithFreePickupPoint) {
      setRemovedMinimumOrderModes(new Set(['pickupPoint']));
    }
    setErrors({});
  };

  const currency = store === Store.FR ? Currency.EUR : Currency.USD;
  const currencyIcon = store === Store.FR ? '€' : '$';

  const getEndDateForDuration = (
    duration: ShippingOfferDuration
  ): string | null => {
    const startDate = hubspotStartDate
      ? new Date(hubspotStartDate)
      : new Date();
    const days = offerDurationToDays(duration);
    if (!days) return null;

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + days);

    const formattedDate = endDate.toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
    });

    return formattedDate;
  };

  const getStartDate = (): string | null => {
    return formatMinimumOrderStartDate(
      hubspotStartDate,
      tempPolicy.timeLimitedEnabled,
      tempPolicy.offerDuration,
      i18n.language
    );
  };

  const minimumOrderStartDate = tempPolicy.timeLimitedEnabled
    ? getStartDate()
    : null;

  const content = (
    <>
      <div className="mb-6 space-y-4">
        {/* Show explanatory message when shipping fees are 0 */}
        {allShippingFeesZero && (
          <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800">
            <div className="flex items-start gap-2">
              <span className="text-blue-600">ℹ️</span>
              {t(
                'components.shipping_policy_configurator.disabled_when_zero_fees',
                {
                  currency,
                }
              )}
            </div>
          </div>
        )}

        <div className="space-y-3 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="time-limited"
              checked={tempPolicy.timeLimitedEnabled}
              disabled={isLocked || allShippingFeesZero}
              onCheckedChange={toggleTimeLimited}
              className="h-[16px] w-[16px]"
            />
            <Label
              htmlFor="time-limited"
              className={
                isLocked || allShippingFeesZero
                  ? 'cursor-not-allowed text-gray-400'
                  : 'cursor-pointer'
              }
            >
              {t(
                'components.shipping_policy_configurator.for_limited_duration'
              )}
            </Label>
          </div>

          {tempPolicy.timeLimitedEnabled && (
            <div className="space-y-3 pl-7">
              <p className="text-sm text-gray-600">
                {t('components.shipping_policy_configurator.choose_duration')}
              </p>
              <RadioGroup
                value={currentDurationValue}
                onValueChange={(value) =>
                  handleDurationChange(value as ShippingOfferDuration)
                }
                className="flex flex-col gap-3"
              >
                {(['24h', '48h', '72h'] as ShippingOfferDuration[]).map(
                  (duration) => {
                    const endDate = getEndDateForDuration(duration);
                    return (
                      <div key={duration} className="flex items-center gap-3">
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem
                            value={duration}
                            id={`duration-${duration}`}
                            disabled={isLocked || allShippingFeesZero}
                            className="h-[16px] w-[16px]"
                          />
                          <Label
                            htmlFor={`duration-${duration}`}
                            className="cursor-pointer text-sm font-medium text-gray-700 peer-disabled:cursor-default peer-disabled:text-gray-300 peer-data-[state=checked]:text-gray-900 peer-data-[state=checked]:peer-disabled:text-gray-300"
                          >
                            {duration}
                          </Label>
                        </div>
                        {endDate && (
                          <p className="text-xs text-muted-foreground">
                            {t(
                              'components.shipping_policy_configurator.ends_on_at',
                              { date: endDate }
                            )}
                          </p>
                        )}
                      </div>
                    );
                  }
                )}
              </RadioGroup>
            </div>
          )}
        </div>

        {/* Minimum order amount option */}
        <div className="space-y-3 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="minimum-order"
              checked={tempPolicy.minimumOrderEnabled}
              disabled={isLocked || allShippingFeesZero}
              onCheckedChange={toggleMinimumOrder}
              className="h-[16px] w-[16px]"
            />
            <Label
              htmlFor="minimum-order"
              className={
                isLocked || allShippingFeesZero
                  ? 'cursor-not-allowed text-gray-400'
                  : 'cursor-pointer'
              }
            >
              {t('components.other.min_purchase_amount')}
            </Label>
          </div>

          {tempPolicy.minimumOrderEnabled && (
            <div className="space-y-3 pl-7">
              <div className="space-y-2">
                <label
                  htmlFor={
                    isHomeMinimumRemoved ? undefined : 'minimum-order-amount'
                  }
                  className="block text-xs font-semibold uppercase text-gray-500"
                >
                  {showPickupPointMinimumAmount
                    ? t(
                        'components.shipping_policy_configurator.home_delivery_minimum_label'
                      )
                    : t(
                        'components.shipping_policy_configurator.minimum_amount_label'
                      )}
                </label>
                {isHomeMinimumRemoved ? (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <span>
                      {t(
                        'components.shipping_policy_configurator.home_delivery_paid_helper'
                      )}
                    </span>
                    <button
                      type="button"
                      className="min-h-4 text-left text-xs font-semibold leading-4 tracking-normal text-[#13482B] hover:underline disabled:cursor-not-allowed disabled:text-gray-300"
                      disabled={isLocked || allShippingFeesZero}
                      onClick={() => restoreMinimumOrderAmount('home')}
                    >
                      {t(
                        'components.shipping_policy_configurator.set_free_shipping'
                      )}
                    </button>
                  </div>
                ) : (
                  <>
                    <div
                      className={cn(
                        showPickupPointMinimumAmount &&
                          'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2'
                      )}
                    >
                      <Input
                        id="minimum-order-amount"
                        type="number"
                        min="1"
                        step="1"
                        value={tempPolicy.minimumOrderAmount ?? ''}
                        disabled={isLocked || allShippingFeesZero}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const newValue =
                            e.target.value === ''
                              ? null
                              : Number(e.target.value);
                          handleMinimumOrderChange(newValue);
                        }}
                        onBlur={() => {
                          if (tempPolicy.minimumOrderAmount === null) {
                            setErrors({
                              ...errors,
                              minimumOrderAmount: t(
                                'components.shipping_policy_configurator.error_required'
                              ),
                            });
                          }
                        }}
                        placeholder={t(
                          'components.shipping_policy_configurator.amount_placeholder'
                        )}
                        className={`bg-white dark:bg-white ${
                          errors.minimumOrderAmount
                            ? 'border-red-500 focus:ring-red-500'
                            : ''
                        }`}
                        endAdornment={
                          <InputAdornment position="end">
                            {currencyIcon}
                          </InputAdornment>
                        }
                      />
                      {showPickupPointMinimumAmount && (
                        <button
                          type="button"
                          aria-label={`${t('general.delete')} ${t(
                            'components.shipping_policy_configurator.home_delivery_minimum_label'
                          )}`}
                          title={t('general.delete')}
                          disabled={isLocked || allShippingFeesZero}
                          className="rounded p-1 text-gray-500 hover:text-gray-900 disabled:cursor-not-allowed disabled:text-gray-300"
                          onClick={() => removeMinimumOrderAmount('home')}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {errors.minimumOrderAmount && (
                      <div className="mt-1 text-xs text-red-500">
                        {errors.minimumOrderAmount}
                      </div>
                    )}
                  </>
                )}
              </div>

              {tempPolicy.pickupPointEnabled &&
                !showPickupPointMinimumAmount && (
                  <button
                    type="button"
                    className="min-h-4 w-fit max-w-full text-left text-xs font-semibold leading-4 tracking-normal text-[#13482B] hover:underline disabled:cursor-not-allowed disabled:text-gray-300 sm:whitespace-nowrap"
                    disabled={isLocked || allShippingFeesZero}
                    onClick={() => {
                      setRemovedMinimumOrderModes(new Set());
                      setTempPolicy((current) => ({
                        ...current,
                        minimumOrderSplitByDeliveryMode: true,
                        pickupPointMinimumOrderAmount:
                          current.pickupPointMinimumOrderAmount ??
                          current.minimumOrderAmount ??
                          1,
                      }));
                    }}
                  >
                    {t(
                      'components.shipping_policy_configurator.define_different_minimum_by_delivery_mode'
                    )}
                  </button>
                )}

              {tempPolicy.pickupPointEnabled &&
                showPickupPointMinimumAmount && (
                  <div className="space-y-2">
                    <label
                      htmlFor={
                        isPickupPointMinimumRemoved
                          ? undefined
                          : 'pickup-point-minimum-order-amount'
                      }
                      className="block text-xs font-semibold uppercase text-gray-500"
                    >
                      {t(
                        'components.shipping_policy_configurator.pickup_point_minimum_label'
                      )}
                    </label>
                    {isPickupPointMinimumRemoved ? (
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span>
                          {t(
                            tempPolicy.pickupPointDeliveryPrice === 0
                              ? 'components.shipping_policy_configurator.pickup_point_delivery_offered_helper'
                              : 'components.shipping_policy_configurator.pickup_point_delivery_paid_helper'
                          )}
                        </span>
                        {!isPickupPointDeliveryFree && (
                          <button
                            type="button"
                            className="min-h-4 text-left text-xs font-semibold leading-4 tracking-normal text-[#13482B] hover:underline disabled:cursor-not-allowed disabled:text-gray-300"
                            disabled={isLocked || allShippingFeesZero}
                            onClick={() =>
                              restoreMinimumOrderAmount('pickupPoint')
                            }
                          >
                            {t(
                              'components.shipping_policy_configurator.set_free_shipping'
                            )}
                          </button>
                        )}
                      </div>
                    ) : isPickupPointDeliveryFree ? (
                      <p className="text-sm text-muted-foreground">
                        {t(
                          'components.shipping_policy_configurator.pickup_point_delivery_offered_helper'
                        )}
                      </p>
                    ) : (
                      <>
                        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                          <Input
                            id="pickup-point-minimum-order-amount"
                            type="number"
                            min="1"
                            step="1"
                            value={
                              tempPolicy.pickupPointMinimumOrderAmount ?? ''
                            }
                            disabled={isLocked || allShippingFeesZero}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              const newValue =
                                e.target.value === ''
                                  ? null
                                  : Number(e.target.value);
                              handlePickupPointMinimumOrderChange(newValue);
                            }}
                            onBlur={() => {
                              if (
                                tempPolicy.pickupPointMinimumOrderAmount ===
                                null
                              ) {
                                setErrors({
                                  ...errors,
                                  pickupPointMinimumOrderAmount: t(
                                    'components.shipping_policy_configurator.error_required'
                                  ),
                                });
                              }
                            }}
                            placeholder={t(
                              'components.shipping_policy_configurator.amount_placeholder'
                            )}
                            className={`bg-white dark:bg-white ${
                              errors.pickupPointMinimumOrderAmount
                                ? 'border-red-500 focus:ring-red-500'
                                : ''
                            }`}
                            endAdornment={
                              <InputAdornment position="end">
                                {currencyIcon}
                              </InputAdornment>
                            }
                          />
                          <button
                            type="button"
                            aria-label={`${t('general.delete')} ${t(
                              'components.shipping_policy_configurator.pickup_point_minimum_label'
                            )}`}
                            title={t('general.delete')}
                            disabled={isLocked || allShippingFeesZero}
                            className="rounded p-1 text-gray-500 hover:text-gray-900 disabled:cursor-not-allowed disabled:text-gray-300"
                            onClick={() =>
                              removeMinimumOrderAmount('pickupPoint')
                            }
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        {errors.pickupPointMinimumOrderAmount && (
                          <div className="mt-1 text-xs text-red-500">
                            {errors.pickupPointMinimumOrderAmount}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

              {/* Only show start date if time-limited is also enabled (Phase 2 starts after Phase 1) */}
              {minimumOrderStartDate && (
                <p className="text-xs text-muted-foreground">
                  {t('components.shipping_policy_configurator.starts_on_at', {
                    date: minimumOrderStartDate,
                  })}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Info block when both options are enabled */}
        {bothOptionsEnabled && (
          <div className="rounded-md bg-blue-50 p-3 text-sm">
            <div className="flex items-start gap-2">
              <span className="text-blue-600">ℹ️</span>
              <div className="flex-1 space-y-1">
                <p className="font-medium text-blue-900">
                  {t('components.shipping_policy_configurator.info_title')}
                </p>
                <p className="text-blue-800">
                  {t(
                    'components.shipping_policy_configurator.info_description'
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button
          type="button"
          appearance="outline"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          {cancelLabel ?? t('general.cancel')}
        </Button>
        <Button
          type="button"
          appearance="primary"
          onClick={
            allShippingFeesZero && !embedded
              ? () => onOpenChange(false)
              : handleSave
          }
          disabled={isLocked || isSubmitting}
        >
          {confirmLabel ??
            (allShippingFeesZero ? t('general.close') : t('general.validate'))}
        </Button>
      </DialogFooter>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="sm:max-w-[522px]">
        <DialogHeader>
          <DialogTitle>
            {t('ui-v2.settings.blocks-shipping-policy.free-shipping.title')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'components.import_flow.products_option_selection.choose_option'
            )}
            :
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}

export default ShippingPolicyDisplay;
